import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { listMyOrders, listMySlots } from './api';

/**
 * The inputs to the unpaid-order payment lock, and the one way to tear them down.
 *
 * Two surfaces ASK the unpaid-order question — the hard lock
 * (`RequireOrderSettled`) and the soft dialog (`UnpaidOrdersGate`) — and the
 * payment page CHANGES the answer. Before this module the key, the status pair
 * and the staleTime were duplicated verbatim in the two readers and invalidated
 * by nobody, which is the shared root of the "Continue bounces back" bug.
 */

/** The two statuses that mean "created, not paid". Same pair the order list,
 *  the manual-payment guards and the gateway initiate all key on. */
export const UNPAID_STATUSES = ['pending', 'to_pay'];

/** sessionStorage key suppressing the soft unpaid-orders dialog for the rest of
 *  the tab session. Three surfaces touch it: the dialog sets it on dismiss,
 *  `logout()` clears it so a re-login re-arms the prompt, and the login
 *  resume-redirect sets it — being sent to the payment page IS the prompt, the
 *  dialog must not double-fire. Defined in ./api (logout's home) to avoid an
 *  import cycle; re-exported here beside the lock's other shared inputs. */
export { UNPAID_DIALOG_SESSION_FLAG } from './api';

export const UNPAID_ORDERS_KEY = ['unpaid-orders'] as const;
/** Shared with `useMySlots`, deliberately — same key, same fetcher, one cache
 *  entry. The lock reads it through its OWN observer so it can be gated on the
 *  tenant flag; a tenant with the lock off must not fetch slots it won't use. */
export const MY_SLOTS_KEY = ['my-slots'] as const;

export const unpaidOrdersQuery = queryOptions({
  queryKey: UNPAID_ORDERS_KEY,
  queryFn: () => listMyOrders(undefined, { statuses: UNPAID_STATUSES, limit: 50 }),
  staleTime: 60_000,
});

export const mySlotsQuery = queryOptions({
  queryKey: MY_SLOTS_KEY,
  queryFn: listMySlots,
  // Matches `useMySlots` so the two observers agree about freshness on the one
  // shared entry.
  staleTime: 30_000,
});

/**
 * Tear down the lock's cached answer, from the surface that just changed it.
 *
 * **`resetQueries`, NOT `invalidateQueries` — and that difference IS the bug.**
 * `RequireOrderSettled` is unmounted for the entire duration of `/pay/:orderId`
 * (its destination is declared outside it, on purpose, so the redirect can't
 * loop). Its queries therefore have ZERO observers: no interval refetch, no
 * focus refetch. An INVALIDATED entry still hands back its stale array with
 * `isPending: false` the moment the guard remounts; `decideOrderGate` reads that
 * as "the answer is known" and bounces the member back to the order they just
 * paid for. Only `queryClient.clear()` on logout ever broke that loop — which is
 * exactly why signing out "fixed" it.
 *
 * `reset` restores `status: 'pending', data: undefined`, which is the state the
 * guard is already built for: hold on the splash, fetch once, decide. An errored
 * refetch settles with no data, which reads as unknown and PASSES — it fails
 * open, where an awaited `refetchQueries` would fail closed and re-trap the
 * member on a flaky connection.
 *
 * `removeQueries` would also clear it, but `QueryCache.remove` never calls
 * `setState`, so a LIVE observer is not notified — and `['my-slots']` is
 * observed by most member pages. `reset` notifies, and `resetQueries` refetches
 * only ACTIVE matches, so an unobserved query costs zero network here.
 *
 * BOTH keys, not just orders: when a virtual kit is approved the order becomes
 * paid AND (under `kit_auto_create_slot.virtual`) a slot is created, so the
 * "no active slot" condition goes stale at the same instant. Resetting only the
 * orders would leave Continue bouncing for the other reason.
 */
export function resetLockInputs(qc: QueryClient): void {
  void qc.resetQueries({ queryKey: UNPAID_ORDERS_KEY });
  void qc.resetQueries({ queryKey: MY_SLOTS_KEY });
}
